<template>
  <section class="document-type-grid">
    <div class="grid-head">
      <h2 class="grid-title">สร้างเอกสาร</h2>
      <span class="grid-sub">ดึงข้อมูลคนไข้ให้อัตโนมัติ แล้วเซ็นได้ทันที</span>
    </div>

    <div class="grid-columns">
      <!-- ใบรับรอง -->
      <div v-if="medicalCertificates.length > 0" class="grid-group" :class="{ 'is-open': openGroup === 'cert' }">
        <button type="button" class="group-toggle" :aria-expanded="openGroup === 'cert' ? 'true' : 'false'" @click="toggleGroup('cert')">
          <span class="group-icon" :style="groupIconStyle('cert')" aria-hidden="true">
            <i :class="groupTheme.cert.icon"></i>
          </span>
          <span class="group-label">ใบรับรอง</span>
          <span class="group-count">{{ medicalCertificates.length }}</span>
          <i class="fas fa-chevron-down group-chevron" aria-hidden="true"></i>
        </button>
        <div class="group-items">
          <DocumentTypeCard
            v-for="item in medicalCertificates"
            :key="item.value"
            :title="item.text"
            :value="item.value"
            :locked="item.locked"
            :is-generating="generatingDocument === item.value"
            @generate="handleGenerate"
            @locked="showUpgradeNotice(item.text)"
          />
        </div>
      </div>

      <!-- ใบยินยอม -->
      <div v-if="consentForms.length > 0" class="grid-group" :class="{ 'is-open': openGroup === 'consent' }">
        <button type="button" class="group-toggle" :aria-expanded="openGroup === 'consent' ? 'true' : 'false'" @click="toggleGroup('consent')">
          <span class="group-icon" :style="groupIconStyle('consent')" aria-hidden="true">
            <i :class="groupTheme.consent.icon"></i>
          </span>
          <span class="group-label">ใบยินยอม</span>
          <span class="group-count">{{ consentForms.length }}</span>
          <i class="fas fa-chevron-down group-chevron" aria-hidden="true"></i>
        </button>
        <div class="group-items">
          <DocumentTypeCard
            v-for="item in consentForms"
            :key="item.value"
            :title="item.text"
            :value="item.value"
            :locked="item.locked"
            :is-generating="generatingDocument === item.value"
            @generate="handleGenerate"
            @locked="showUpgradeNotice(item.text)"
          />
        </div>
      </div>

      <!-- แบบฟอร์มคลินิก -->
      <div
        v-if="templateDocuments.length > 0 || showTemplateUpload"
        class="grid-group"
        :class="{ 'is-open': openGroup === 'template' }"
      >
        <button type="button" class="group-toggle" :aria-expanded="openGroup === 'template' ? 'true' : 'false'" @click="toggleGroup('template')">
          <span class="group-icon" :style="groupIconStyle('template')" aria-hidden="true">
            <i :class="groupTheme.template.icon"></i>
          </span>
          <span class="group-label">แบบฟอร์มคลินิก</span>
          <span class="group-count">{{ templateDocuments.length }}</span>
          <i class="fas fa-chevron-down group-chevron" aria-hidden="true"></i>
        </button>
        <div class="group-items">
          <div v-if="showTemplateFilter" class="template-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              v-model="templateFilter"
              type="search"
              class="form-control"
              placeholder="ค้นหาแบบฟอร์ม"
              aria-label="ค้นหาแบบฟอร์มคลินิก"
            />
          </div>
          <DocumentTypeCard
            v-for="template in visibleTemplates"
            :key="template.id"
            :title="getTemplateName(template)"
            :value="template.id"
            :locked="!canGenerateDocument"
            :is-generating="signingTemplate === template.id"
            :deletable="canManageClinicForm"
            @generate="handleTemplateSign"
            @locked="showUpgradeNotice('การเซ็นแบบฟอร์มเก็บเข้าแฟ้มคนไข้')"
            @delete="handleTemplateDelete"
          />
          <p v-if="hasNoTemplateMatch" class="template-empty">ไม่พบแบบฟอร์มที่ค้นหา</p>
          <p v-else-if="templateDocuments.length === 0" class="template-empty">
            ยังไม่มีแบบฟอร์มคลินิก
          </p>
          <button
            v-if="canToggleTemplates"
            type="button"
            class="template-toggle"
            @click="showAllTemplates = !showAllTemplates"
          >
            {{ showAllTemplates ? "ย่อ" : `ดูทั้งหมด (${filteredTemplates.length})` }}
          </button>
          <DocumentTypeCard
            v-if="showTemplateUpload"
            title="อัปโหลดแบบฟอร์ม (PDF, JPG, PNG)"
            value="upload_template"
            :is-upload="true"
            :locked="!hasClinicFormPlan"
            @generate="handleTemplateUpload"
            @locked="showUpgradeNotice('การเพิ่มแบบฟอร์มคลินิก')"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import DocumentTypeCard from "./DocumentTypeCard.vue";
import { DOCUMENT_GROUP_THEME } from "@/utils/categoryTheme";
import ClinicFormPermissionMixin from "@/mixins/ClinicFormPermissionMixin";
import UpgradeNoticeMixin from "@/mixins/UpgradeNoticeMixin";

// เกินเท่านี้ค่อยโชว์ช่องค้นหา — สาขาที่มีไม่กี่แบบฟอร์มไม่ต้องเจอ UI เพิ่ม
const TEMPLATE_FILTER_THRESHOLD = 6;
// จำนวนแบบฟอร์มที่โชว์ตอนย่อ ก่อนต้องกด "ดูทั้งหมด"
const TEMPLATE_COLLAPSED_COUNT = 5;

export default {
  name: "DocumentTypeGrid",
  mixins: [ClinicFormPermissionMixin, UpgradeNoticeMixin],
  components: {
    DocumentTypeCard,
  },
  props: {
    documentTypes: {
      type: Array,
      default: () => [],
    },
    generatingDocument: {
      type: String,
      default: null,
    },
    templateDocuments: {
      type: Array,
      default: () => [],
    },
    // mediaFile id ของแบบฟอร์มเป็น Number ส่วน key ของเอกสารที่ generate เป็น String
    signingTemplate: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      templateFilter: "",
      showAllTemplates: false,
      // มือถือ: เปิดทีละกลุ่ม (จอกว้างแสดงทุกกลุ่มตลอด)
      openGroup: null,
      groupTheme: DOCUMENT_GROUP_THEME,
    };
  },
  computed: {
    ...mapGetters({
      canGenerateDocument: "moduleBranch/canGenerateDocument",
    }),
    // ที่ต้องใช้แพ็กเกจยังแสดง พร้อมป้าย "อัปเกรด" ให้รู้ว่ามีให้ใช้
    medicalCertificates() {
      return this.flattenedDocuments.filter(item =>
        ["medicalCert", "referralLetter"].includes(item.value)
      );
    },
    consentForms() {
      return this.flattenedDocuments.filter(item => item.value.includes("Consent"));
    },
    flattenedDocuments() {
      const flattened = [];
      this.documentTypes.forEach(item => {
        if (item.options) {
          item.options.forEach(option => flattened.push(option));
        } else if (item.value !== "empty") {
          flattened.push(item);
        }
      });
      return flattened;
    },
    // ผู้จัดการสาขา / RDM / C_LEVEL เห็นปุ่ม — แพ็ก FREE เห็นเป็นปุ่มติดล็อกแพ็ก
    showTemplateUpload() {
      return this.canManageClinicForm;
    },
    showTemplateFilter() {
      return this.templateDocuments.length > TEMPLATE_FILTER_THRESHOLD;
    },
    normalizedTemplateFilter() {
      return this.templateFilter.trim().toLowerCase();
    },
    isTemplateFiltering() {
      return this.normalizedTemplateFilter.length > 0;
    },
    filteredTemplates() {
      if (!this.isTemplateFiltering) return this.templateDocuments;
      const keyword = this.normalizedTemplateFilter;
      return this.templateDocuments.filter(template =>
        this.getTemplateName(template).toLowerCase().includes(keyword)
      );
    },
    // ตอนค้นหาให้โชว์ผลทั้งหมด — คำค้นย่อรายการให้อยู่แล้ว ไม่ต้องย่อซ้ำ
    visibleTemplates() {
      if (this.isTemplateFiltering || this.showAllTemplates) {
        return this.filteredTemplates;
      }
      return this.filteredTemplates.slice(0, TEMPLATE_COLLAPSED_COUNT);
    },
    canToggleTemplates() {
      return !this.isTemplateFiltering && this.filteredTemplates.length > TEMPLATE_COLLAPSED_COUNT;
    },
    hasNoTemplateMatch() {
      return this.isTemplateFiltering && this.filteredTemplates.length === 0;
    },
  },
  methods: {
    groupIconStyle(group) {
      const theme = DOCUMENT_GROUP_THEME[group];
      return { color: theme.color, background: theme.tint };
    },
    toggleGroup(group) {
      this.openGroup = this.openGroup === group ? null : group;
    },
    getTemplateName(template) {
      return template.name || template.remark || "";
    },
    handleGenerate(documentType) {
      this.$emit("generate", documentType);
    },
    handleTemplateSign(templateId) {
      this.$emit("template-sign", templateId);
    },
    handleTemplateUpload() {
      this.$emit("template-upload");
    },
    handleTemplateDelete(templateId) {
      this.$emit("template-delete", templateId);
    },
  },
  emits: ["generate", "template-sign", "template-upload", "template-delete"],
};
</script>

<style scoped>
.grid-head {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.grid-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #101828;
}
.grid-sub {
  font-size: 0.875rem;
  color: #5d6679;
}
.grid-columns {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  align-items: start;
}
.grid-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  padding: 1rem;
  background: #fff;
  border: 1px solid #e8eaef;
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.group-toggle {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.125rem 0.25rem 0.375rem;
  border: 0;
  background: transparent;
  pointer-events: none;
  text-align: left;
}
.group-icon {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.group-label {
  flex: 1 1 auto;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #101828;
}
.group-count {
  padding: 0.0625rem 0.5625rem;
  border-radius: 999px;
  background: #f1f3f6;
  font-size: 0.75rem;
  font-weight: 600;
  color: #3d4557;
}
.group-chevron {
  display: none;
}
.group-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.template-search {
  position: relative;
}
.template-search i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #5d6679;
  font-size: 0.8125rem;
}
.template-search .form-control {
  height: 38px;
  padding-left: 2.125rem;
  border-radius: 999px;
  border-color: #d7dbe3;
  font-size: 0.8125rem;
}
.template-empty {
  margin: 0;
  padding: 0 0.75rem;
  font-size: 0.8125rem;
  color: #5d6679;
}
.template-toggle {
  align-self: flex-start;
  min-height: 30px;
  padding: 0 0.75rem;
  border: 0;
  background: transparent;
  font-size: 0.8125rem;
  color: var(--primary, #0b63c5);
}

@media (max-width: 991.98px) {
  .grid-columns {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* มือถือ: แต่ละกลุ่มเป็นแถวกดเปิด/ปิด */
@media (max-width: 575.98px) {
  .grid-head {
    margin-bottom: 0.625rem;
  }
  .grid-title {
    font-size: 0.8125rem;
    color: #5d6679;
    letter-spacing: 0;
  }
  .grid-sub {
    display: none;
  }
  .grid-columns {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  .grid-group {
    padding: 0;
    border-radius: 14px;
    gap: 0;
  }
  .group-toggle {
    pointer-events: auto;
    min-height: 54px;
    padding: 0 0.75rem;
  }
  .group-icon {
    width: 32px;
    height: 32px;
  }
  .group-label {
    font-weight: 500;
  }
  .group-chevron {
    display: inline-block;
    color: #9aa2b1;
    transition: transform 0.15s;
  }
  .grid-group.is-open .group-chevron {
    transform: rotate(180deg);
  }
  .group-items {
    display: none;
    padding: 0 0.75rem 0.75rem;
  }
  .grid-group.is-open .group-items {
    display: flex;
  }
  .template-search .form-control {
    font-size: 16px;
  }
}
</style>
